import { useNavigate } from '@tanstack/react-router';
import type { WorkOrderListItemDto, WorkOrderAction } from '@signalvote/shared';
import { WorkOrderType, canActOnWorkOrderType } from '@signalvote/shared';
import { useCreateWorkOrder, useWorkOrders, useWorkOrderTransition } from '@/hooks/queries';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignStore } from '@/stores';
import { Button, PendingButton } from '@/components/ui';
import { WorkOrderJobsList } from './WorkOrderJobsList';

/**
 * Data-connected container for the Work Orders jobs list: fetches the campaign's Work
 * Orders through {@link useWorkOrders} and drives the operator's hand-run transitions
 * through {@link useWorkOrderTransition}. It also owns the self-serve "New text send" seam —
 * {@link useCreateWorkOrder} mints a fresh Draft and the container navigates to its build
 * screen — and the "Open" affordance that reopens an in-progress Draft to keep editing it
 * (concurrent drafts are allowed; each has its own build URL). The presentational
 * {@link WorkOrderJobsList} stays prop-driven so it is trivially testable; this container owns
 * the loading/error states and threads each row's current `concurrencyVersion` into the
 * transition call so a stale token is caught server-side (409).
 */
export function WorkOrderJobsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Operators (SignalVote platform admins) see the internal spine + hand-driven transitions;
  // campaign customers (full admins) see the friendly customer view and none of the operator
  // controls. The list renders the operator affordances only when this is true.
  const isPlatformAdmin = user?.isPlatformAdmin ?? false;
  // The area is open to any full admin, but the campaign's Work Orders flag still gates Text
  // send (ADR-090) — so starting one, and a Text send row's actions, follow the per-type gate.
  const workOrdersEnabled = useCampaignStore((s) => s.selectedCampaign?.workOrdersEnabled ?? false);
  const isFullAdmin = useCampaignStore((s) => s.isFullAdmin());
  const canActOn = (type: WorkOrderType) =>
    canActOnWorkOrderType(type, { isPlatformAdmin, isFullAdmin, workOrdersEnabled });
  const { data, isLoading, isError } = useWorkOrders();
  const { mutate, isPending, variables, error } = useWorkOrderTransition();
  const { mutate: create, isPending: isCreating, error: createError } = useCreateWorkOrder();

  const openBuild = (id: string) =>
    navigate({ to: '/app/work-orders/$workOrderId', params: { workOrderId: id } });

  // New text send: create an empty Draft (the price is server-owned, so no amount) and land
  // the admin on its build screen. Concurrent drafts are allowed — each create is independent.
  const handleNewTextSend = () => {
    create({ amount: null, messageText: null, sendDate: null, sendTime: null }, { onSuccess: (workOrder) => openBuild(workOrder.id) });
  };

  if (isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading work orders…
      </p>
    );
  }

  if (isError || !data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Couldn’t load work orders.
      </p>
    );
  }

  const handleTransition = (workOrder: WorkOrderListItemDto, action: WorkOrderAction) => {
    mutate({
      id: workOrder.id,
      body: { action, expectedConcurrencyVersion: workOrder.concurrencyVersion },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Work Orders</h2>
        <div className="flex flex-wrap items-center gap-2">
          {canActOn(WorkOrderType.VoterData) && (
            <Button variant="outline" onClick={() => navigate({ to: '/app/work-orders/voter-data' })}>
              Build a voter list
            </Button>
          )}
          {canActOn(WorkOrderType.TextSend) && (
            <PendingButton pending={isCreating} onClick={handleNewTextSend}>
              New text send
            </PendingButton>
          )}
        </div>
      </div>

      {createError && (
        <p role="alert" className="text-sm text-destructive">
          Couldn’t start a new text send. Please try again.
        </p>
      )}

      <WorkOrderJobsList
        workOrders={data.items}
        isPlatformAdmin={isPlatformAdmin}
        canActOn={canActOn}
        onTransition={handleTransition}
        onOpen={(workOrder) => openBuild(workOrder.id)}
        pendingId={isPending ? (variables?.id ?? null) : null}
        errorMessage={error ? error.message : null}
      />
    </div>
  );
}
